/**
 * How to point each AI client at Ptitim — the exact words to type.
 *
 * Vladimir, 2026-09-21: *"I want more instructions and maybe some commands to
 * copy in the connected apps mcp part for different ai like Claude, Claude
 * code, ChatGPT codex Gemini others and general so users can just copy code or
 * follow clear steps"*. The page named the URL and stopped there, which is
 * enough for somebody who already knows what MCP is and nothing at all for
 * anybody else.
 *
 * ## Two ways in, and the page says which each client needs
 *
 * **A connector** — the client opens a browser, you sign in, you approve. The
 * server is a full OAuth 2.1 authorization server with discovery and dynamic
 * registration, so these clients need the URL and nothing else, and the grant
 * shows up under Connected apps where it can be taken back.
 *
 * **A personal token** — `Authorization: Bearer ptitim_mcp_…`, for a client
 * that cannot open a browser. It carries the scopes it was made with and is
 * revoked from the same page.
 *
 * ## Why a command per client rather than one set of facts
 *
 * Because "add an MCP server" is a different act in each one, and the step
 * somebody gets wrong is never the URL — it is the transport flag, the file to
 * put it in, or which of the two credentials that client can use. The general
 * block at the end is the set of facts, for anything not listed.
 */
export interface McpStep {
  /** One instruction, in plain words. */
  text: string;
  /** What to copy, if this step is a thing to paste. */
  code?: string;
  /** Where it goes — a file path, or a shell. */
  where?: string;
}

export interface McpClient {
  key: string;
  name: string;
  /** Connector: the client signs in for itself. Token: it needs one pasted in. */
  credential: 'connector' | 'token' | 'either';
  steps: McpStep[];
}

/** The placeholder a snippet carries until a real token exists to put in it. */
export const TOKEN_PLACEHOLDER = 'ptitim_mcp_your_token_here';

export function mcpClients(url: string, token: string | null): McpClient[] {
  const key = token ?? TOKEN_PLACEHOLDER;
  return [
    {
      key: 'claude',
      name: 'Claude',
      credential: 'connector',
      steps: [
        { text: 'In Claude — the web app or the desktop one — open Settings, then Connectors.' },
        { text: 'Add a custom connector, call it Ptitim, and give it this URL:', code: url },
        {
          text: 'Connect. Claude opens a Ptitim page, you sign in, and you choose what to allow. The grant appears below, and Disconnect ends it at once.',
        },
        // ⚠ Found in Aleksandr's call (2026-10-08): in a company's Claude the
        // Add button is missing for anybody but an owner — not a Ptitim fault.
        {
          text: 'Claude for work (Team or Enterprise): only an owner of the Claude organisation can add a custom connector. Send them the URL above; once it is added, everybody presses Connect for themselves.',
        },
      ],
    },
    {
      key: 'claude-code',
      name: 'Claude Code',
      credential: 'either',
      steps: [
        {
          text: 'One command, then /mcp inside Claude Code to sign in:',
          code: `claude mcp add --transport http ptitim ${url}`,
          where: 'a terminal',
        },
        {
          text: 'Or, where no browser can open — a server, a container — skip the sign-in and carry a personal token instead (make one below):',
          code: `claude mcp add --transport http ptitim ${url} \\\n  --header "Authorization: Bearer ${key}"`,
          where: 'a terminal',
        },
        {
          text: 'Check it took: /mcp lists ptitim as connected, and "what did I commit to this week?" reads the ledger.',
        },
      ],
    },
    {
      key: 'chatgpt',
      name: 'ChatGPT',
      credential: 'connector',
      steps: [
        {
          text: 'Connectors are on the paid plans, and custom ones may sit behind developer mode. In ChatGPT open Settings, then Connectors.',
        },
        { text: 'Add a custom connector with this URL:', code: url },
        { text: 'Sign in and approve. It appears below, and Disconnect ends it.' },
      ],
    },
    {
      key: 'codex',
      name: 'Codex CLI',
      credential: 'token',
      steps: [
        {
          text: 'Make a token below, then add this to Codex’s config:',
          code: codexToml(url, key),
          where: '~/.codex/config.toml',
        },
        {
          text: 'npx runs the bridge that turns the http server into the stdio one Codex expects; it needs Node. Restart Codex and ask it to list your meetings.',
        },
      ],
    },
    {
      key: 'gemini',
      name: 'Gemini CLI',
      credential: 'token',
      steps: [
        {
          text: 'Make a token below, then add this to Gemini’s settings:',
          code: geminiJson(url, key),
          where: '~/.gemini/settings.json',
        },
        { text: 'Restart the CLI. /mcp lists ptitim and the tools it brought.' },
      ],
    },
    {
      key: 'editors',
      name: 'Cursor · VS Code · Windsurf',
      credential: 'token',
      steps: [
        {
          text: 'Make a token below, then put this in the editor’s MCP file — .cursor/mcp.json, .vscode/mcp.json, or the equivalent:',
          code: editorJson(url, key),
          where: '.cursor/mcp.json',
        },
        { text: 'Reload the window. The tools appear in the agent’s tool list.' },
      ],
    },
    {
      key: 'anything',
      name: 'Anything else',
      credential: 'either',
      steps: [
        {
          text: 'The whole of it: one endpoint, JSON-RPC over HTTP POST, and either credential.',
          code: [
            `URL        ${url}`,
            'Transport  streamable HTTP — one POST per message, JSON back',
            'Auth       OAuth 2.1 (discovery below), or Authorization: Bearer ptitim_mcp_…',
            `Discovery  ${origin(url)}/.well-known/oauth-protected-resource`,
          ].join('\n'),
        },
        {
          text: 'A client that can only run a command — stdio — reaches it through the bridge:',
          code: `npx -y mcp-remote ${url} --header "Authorization: Bearer ${key}"`,
          where: 'a terminal',
        },
        {
          text: 'And to prove the door opens before touching any config:',
          code: `curl -s ${url} \\\n  -H "Authorization: Bearer ${key}" \\\n  -H 'content-type: application/json' \\\n  -d '{"jsonrpc":"2.0","id":1,"method":"tools/list"}'`,
          where: 'a terminal',
        },
      ],
    },
  ];
}

const codexToml = (url: string, key: string): string =>
  [
    '[mcp_servers.ptitim]',
    'command = "npx"',
    `args = ["-y", "mcp-remote", "${url}", "--header", "Authorization: Bearer ${key}"]`,
  ].join('\n');

const geminiJson = (url: string, key: string): string =>
  JSON.stringify(
    { mcpServers: { ptitim: { httpUrl: url, headers: { Authorization: `Bearer ${key}` } } } },
    null,
    2,
  );

const editorJson = (url: string, key: string): string =>
  JSON.stringify(
    { mcpServers: { ptitim: { url, headers: { Authorization: `Bearer ${key}` } } } },
    null,
    2,
  );

/** `https://api-ptitim…` out of `https://api-ptitim…/v1/mcp`, for the discovery line. */
function origin(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return url.replace(/\/v1\/mcp$/, '');
  }
}
